import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, FlowGrid, H6, Row } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import { useFormState, useZionFormContext } from '@fs/zion-form'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import getEventsDispatches from '../utils/saveEvents'
import { headers } from '../../../../../shared/services/config'
import { errorCollector } from '../../../../../shared/utils/utils'
import EventForm from './EventForm'
import { touchFieldsWithErrors } from '../../../../utils/formResolvers.utils'

/**
 * PrimaryEventEdit
 *
 * Displays primary event in edit mode with warning alert, description text,
 * form, and save/cancel buttons.
 *
 * The Save button here saves ONLY the primary event changes to SLS, not the entire form.
 * This allows users to save changes to an existing primary event without saving the
 * entire events form.
 *
 * @param {Object} props
 * @param {Object} props.event - Event field data
 * @param {number} props.index - Event index in field array
 * @param {Object} props.formArrayMethods - Methods from useFieldArray
 * @param {Array} props.eventAutosuggestOptions - Options for event type selector
 * @param {Object} props.person - Person data
 * @param {string} props.appName - Application name
 * @param {string} props.userId - User ID
 * @param {boolean} props.saveCardDataToSls - Whether to save to SLS
 * @param {string} props.splitScreenImageArk - Split screen image ARK
 * @param {Object} props.defaultValues - Default form values (contains eventsById and primaryEventId)
 * @param {Function} props.onSave - Callback when Save button clicked successfully
 * @param {Function} props.onCancel - Callback when Cancel button clicked
 * @param {Array<Object>} props.latestsPrimaryEvents - The latest primary events for the section
 * @param {Function} props.setLatestsPrimaryEvents - Function to update the latest primary events
 * @returns {JSX.Element}
 */
const PrimaryEventEdit = ({
  event,
  index,
  formArrayMethods,
  onEventRemoved,
  eventAutosuggestOptions,
  person,
  appName,
  userId,
  saveCardDataToSls,
  splitScreenImageArk,
  onSave,
  onCancel,
  latestsPrimaryEvents,
  setLatestsPrimaryEvents,
}) => {
  const [t] = useTranslation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const { getValues, trigger, resetField, control, setValue } = useZionFormContext()
  const { defaultValues: formDefaultValues, dirtyFields, errors } = useFormState({ control })
  const { types, images, currentImageArk } = useSlsStateContext()
  const slsDispatch = useSlsDispatchContext()
  const { getDispatches, commitEdits } = useSlsHighlightsContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const updatePrimaryEventsInForm = useCallback(
    (primaryEvents) => {
      const updatedEvents = primaryEvents.map((pe) => ({
        ...pe,
        primary: true,
        existingEventId: event?.id, // Ensure existingEventId is set for all events to track them properly
      }))

      resetField('events.primary', {
        defaultValue: updatedEvents || [],
        keepDirty: false,
        keepTouched: false,
        keepErrors: false,
      }) // Update form with current values to reset dirty state
      setLatestsPrimaryEvents(updatedEvents || []) // Update latest primary events with current form values
    },
    [event, setLatestsPrimaryEvents, resetField]
  )

  const handleSave = useCallback(async () => {
    const isValid = await trigger('events.primary') // Validate only primary event fields before saving
    touchFieldsWithErrors(errors?.events?.primary, setValue, getValues, 'events.primary') // Touch only primary event fields with errors to show validation messages

    if (!isValid) {
      return
    }

    // Get current form values
    const formData = getValues()

    setError(null)
    setSaving(true)

    // Get highlight dispatches
    const highlightDispatches = getDispatches({
      mainImageArk: currentImageArk,
      splitScreenImageArk,
    })

    // Generate dispatches for primary event only
    const eventsDispatches = getEventsDispatches({
      currentEvents: {
        primary: formData?.events?.primary,
        additional: [], // Don't include additional events
      },
      primaryEventId: formDefaultValues?.primaryEventId,
      defaultValues: {
        primary: formDefaultValues?.events?.primary || [],
        additional: [], // Don't include additional events
      },
      images,
      person,
      appName,
      slsTypes: types,
      dirtyFields: dirtyFields?.events,
      highlightDispatches,
    })

    const dispatches = [...eventsDispatches]

    if (saveCardDataToSls && dispatches.length > 0) {
      const sls = generateSls(dispatches)

      saveSlsAndUpdateState({
        sls,
        dispatches,
        saveChangesFlag,
        slsActionsSplunkDebugger,
        userId,
        dispatch: slsDispatch,
        appName,
        fsUserAgentChain: headers['FS-User-Agent-Chain'],
        successHandler: () => {
          setSaving(false)
          setError(null)

          // Update local state if saveChangesFlag is off
          if (!saveChangesFlag.isOn) {
            slsDispatch({
              type: 'BATCH_DISPATCHES',
              value: dispatches,
            })
          }

          commitEdits()
          // Update form with current values to reset dirty state and update latest primary events
          updatePrimaryEventsInForm(formData?.events?.primary)
          // Call onSave to toggle back to view mode
          onSave()
        },
        errorHandler: (err) => {
          setError(err)
          setSaving(false)
          errorCollector(err, null, {
            codeId: 91,
            sls,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
        },
        saveInitiatorFilename: 'PrimaryEventEdit.js',
      })
    } else if (!saveCardDataToSls) {
      setSaving(false)
      commitEdits()
      // Update form with current values to reset dirty state and update latest primary events
      updatePrimaryEventsInForm(formData?.events?.primary)
      onSave()
    } else {
      // No changes to save
      setSaving(false)
      onSave()
    }
  }, [
    getValues,
    formDefaultValues,
    images,
    person,
    appName,
    types,
    currentImageArk,
    splitScreenImageArk,
    saveCardDataToSls,
    saveChangesFlag,
    slsActionsSplunkDebugger,
    userId,
    slsDispatch,
    getDispatches,
    commitEdits,
    onSave,
    trigger,
    dirtyFields,
    setValue,
    errors,
    updatePrimaryEventsInForm,
  ])

  const handleCancel = useCallback(() => {
    setError(null)
    formArrayMethods.replace(latestsPrimaryEvents) // Reset form to original values by replacing with current fields
    resetField('events.primary', {
      defaultValue: latestsPrimaryEvents,
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
    }) // Reset react-hook-form state for primary events to match the replaced values
    onCancel()
  }, [onCancel, formArrayMethods, latestsPrimaryEvents, resetField])

  return (
    <FlowGrid columns="1" gutters="xxs">
      <H6>{t('eventsForm.primary-event-edit-title', 'Edit Primary Event')}</H6>
      <Alert type="warning" outlined dense>
        {t(
          'eventsForm.primary-event-edit-warning',
          'Editing this information will update the primary event for everyone in the household.'
        )}
      </Alert>
      <EventForm
        event={event}
        index={index}
        formArrayMethods={formArrayMethods}
        onEventRemoved={onEventRemoved}
        eventAutosuggestOptions={eventAutosuggestOptions}
        person={person}
        appName={appName}
        userId={userId}
        saveCardDataToSls={saveCardDataToSls}
        splitScreenImageArk={splitScreenImageArk}
      />
      {error && (
        <Alert type="error" outlined dense>
          {t('events-card.save-error-msg', 'Error saving data. Please try again later.')}
        </Alert>
      )}
      <Row alignX="end" gutters="xxs">
        <Button emphasis="low" color="gray" onClick={handleCancel} disabled={saving}>
          {t('navigation.cancel-button', 'Cancel')}
        </Button>
        <Button emphasis="medium" color="gray" onClick={handleSave} disabled={saving}>
          {t('navigation.save-button', 'Save')}
        </Button>
      </Row>
    </FlowGrid>
  )
}

PrimaryEventEdit.propTypes = {
  event: PropTypes.shape({}).isRequired,
  index: PropTypes.number.isRequired,
  formArrayMethods: PropTypes.shape({}).isRequired,
  onEventRemoved: PropTypes.func,
  eventAutosuggestOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  person: PropTypes.shape({}),
  appName: PropTypes.string,
  userId: PropTypes.string,
  saveCardDataToSls: PropTypes.bool,
  splitScreenImageArk: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  latestsPrimaryEvents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setLatestsPrimaryEvents: PropTypes.func.isRequired,
}

export default PrimaryEventEdit
